/**
 * TreatmentGroupsTable - Handsontable table component
 * Uses all editors, validators, and renderers
 */
import type { FC, RefObject } from 'react';
import { useMemo } from 'react';
import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { ColorEditor } from '@/components/UI/SpreadSheet/editors/ColorEditor';
import type { MetadataField } from '@/model/Metadata.model';
import { _isNil, _notNil } from '@/littledash';
import type { TreatmentGroupKeyedMetadata } from './CreateTreatmentGroups';
import { settings as baseSettings, metadataToColumnSettingMapper } from './CreateTreatmentGroups.utils';
import { type CustomColumnSettings } from '../UI/SpreadSheet/SpreadSheet.model';

interface TreatmentGroupsTableProps {
  groupData: { groups: Array<TreatmentGroupKeyedMetadata> };
  setGroupData: (data: { groups: Array<TreatmentGroupKeyedMetadata> }) => void;
  columnData: {
    colHeaders: Array<string>;
    columns: Array<CustomColumnSettings>;
  };
  updateColumnData: (data: { colHeaders: Array<string>; columns: Array<CustomColumnSettings> }) => void;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setIsInvalid: (invalid: boolean) => void;
  innerRef: RefObject<HotTableRef | null>;
}

const TreatmentGroupsTable: FC<TreatmentGroupsTableProps> = ({
  groupData,
  setGroupData,
  columnData,
  updateColumnData,
  metadata,
  setMetadata,
  setIsInvalid,
  innerRef,
}) => {
  const settings = useMemo<SpreadsheetSettings>(
    () => ({
      ...baseSettings,
      colHeaders: columnData.colHeaders,
      columns: columnData.columns,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      manualColumnResize: true,
    }),
    [columnData]
  );

  const patchMetadataAfterSettingsUpdated = () => {
    // Sync mutated settings back to parent state
    updateColumnData({
      colHeaders: settings.colHeaders as Array<string>,
      columns: settings.columns as Array<CustomColumnSettings>,
    });
  };

  const handleAfterCreateRow = () => {
    requestAnimationFrame(() => {
      const updatedGroups = [...groupData.groups];
      for (let rowIndex = 0; rowIndex < updatedGroups.length - 1; rowIndex++) {
        const group = updatedGroups[rowIndex];
        if (_isNil(group.color)) {
          group.color = ColorEditor.colorForIndex(rowIndex);
        }
        group.no = rowIndex;
      }
      setGroupData({ groups: updatedGroups });
    });
  };

  const handleAfterRemoveRow = () => {
    const hot = innerRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.validateCells();
    }
  };

  return (
    <Spreadsheet
      className="studyGroups"
      testPrefix="treatment-groups"
      data={groupData.groups as unknown as Array<Record<string, unknown>>}
      innerRef={innerRef as RefObject<HotTableRef>}
      settings={settings as SpreadsheetSettings}
      autoAddRow
      metadata={metadata}
      metadataToColumnSettingMapper={metadataToColumnSettingMapper}
      afterCreateRow={handleAfterCreateRow}
      setMetadata={setMetadata}
      setInvalid={setIsInvalid}
      afterRemoveRow={handleAfterRemoveRow}
      patchMetadataAfterSettingsUpdated={patchMetadataAfterSettingsUpdated}
    />
  );
};

export default TreatmentGroupsTable;
